package dev.youndie.site.pages.blog

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initBotsAsStateMachinesPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("blog/BotsAsStateMachines.md", FrontMatterElement.Builder { addScalar("title", "A Telegram bot is a state machine pretending not to be"); addScalar("searchTitle", "Telegram bot in Kotlin as a finite state machine"); addScalar("date", "2026-01-25"); addScalar("description", "telek models a conversation as pure transitions with effects on the side — which is what makes a wizard resumable, testable and portable between two transports."); addScalar("library", "telek"); }))
}

@Page("/blog/bots-as-state-machines")
@Layout(".components.layouts.MarkdownLayout")
@Composable
fun BotsAsStateMachinesPage() {
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Every Telegram bot starts as a ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("when")
        }
        org.jetbrains.compose.web.dom.Text(" over the incoming message and ends as a pile of callbacks with a nullable ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("awaitingCity")
        }
        org.jetbrains.compose.web.dom.Text(" field somewhere in a map. The reason is not laziness. It is that the framework hands you an event and no place to put the fact that this user is halfway through something.")
    }
    org.jetbrains.compose.web.dom.P {
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/telek") {
            org.jetbrains.compose.web.dom.Text("telek")
        }
        org.jetbrains.compose.web.dom.Text(" starts from the shape that was there all along:")
    }
    com.varabyte.kobweb.compose.dom.GenericTag("blockquote") {
        org.jetbrains.compose.web.dom.P {
            org.jetbrains.compose.web.dom.Text("state + input → newState + effects")
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("pure-transitions-effects-on-the-side") }) {
        org.jetbrains.compose.web.dom.Text("Pure transitions, effects on the side")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A transition is a function. It takes the current state and the input, and returns the next state plus a list of things to do — send this message, edit that one, call an API. It does none of them.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("That split is the whole design, and everything useful falls out of it:")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("A transition is testable with no bot, no network and no mocks. It is a function over data.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("A conversation can be replayed from its inputs, because nothing was hidden in a closure.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Persisting a wizard is persisting a value, not a call stack — which is what makes it survive a restart.")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("StateDispatcher")
        }
        org.jetbrains.compose.web.dom.Text(" describes one flow: a multi-step form, a checkout, an onboarding sequence. Flows compose rather than nest, so adding a step is adding a transition rather than another level of indentation.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("two-transports-same-shape") }) {
        org.jetbrains.compose.web.dom.Text("Two transports, same shape")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("telek supports both ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/kotlin-telegram-bot/kotlin-telegram-bot") {
            org.jetbrains.compose.web.dom.Text("kotlin-telegram-bot")
        }
        org.jetbrains.compose.web.dom.Text(" and ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/InsanusMokrassar/TelegramBotAPI") {
            org.jetbrains.compose.web.dom.Text("ktgbotapi")
        }
        org.jetbrains.compose.web.dom.Text(", as separate modules with the same API shape. You pick the client you already use:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""implementation("io.github.youndie.telek:core:<VERSION>")

// pick one transport:
implementation("io.github.youndie.telek:telegram:<VERSION>") // kotlin-telegram-bot
// implementation("io.github.youndie.telek:ktg:<VERSION>")   // ktgbotapi
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("This is only possible because the core never touches the network. If transitions performed their own sends, the transport would be baked into every one of them and there would be no seam to swap.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("native-because-the-core-is-pure") }) {
        org.jetbrains.compose.web.dom.Text("Native, because the core is pure")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("core")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("ktg")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("router")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("router-ktg")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("persistence")
        }
        org.jetbrains.compose.web.dom.Text(" and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("testing")
        }
        org.jetbrains.compose.web.dom.Text(" are published for ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("JVM, linuxX64 and linuxArm64")
        }
        org.jetbrains.compose.web.dom.Text(". A bot can ship as a single native Linux binary — no JRE on the box, a few tens of megabytes of RSS, and a start measured in milliseconds.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("telegram")
        }
        org.jetbrains.compose.web.dom.Text(" and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("router-telegram")
        }
        org.jetbrains.compose.web.dom.Text(" stay JVM-only, because kotlin-telegram-bot is JVM-only. That is not a limitation to work around; it is the honest shape of the dependency, and a plain JVM project resolves the right variant from Gradle module metadata without noticing any of this.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The reason the split lands where it does is the same as before: the pure part has nothing platform-specific in it, so it goes everywhere. The part that speaks HTTP through a JVM library does not.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Source: ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/telek") {
            org.jetbrains.compose.web.dom.Text("github.com/youndie/telek")
        }
        org.jetbrains.compose.web.dom.Text(" · ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://youndie.github.io/telek/") {
            org.jetbrains.compose.web.dom.Text("API docs")
        }
    }
}
